import {
  explainResolution,
  formatEntitlementValue,
  grantTierLabel,
  precedenceTierLabel,
  readTaggedValue,
  type ResolutionExplanation,
  scopeLabel,
} from "./effective";
import type { EffectiveVsGranted } from "./entitlements-api";

export interface ResolutionViewProps {
  readonly view: EffectiveVsGranted | null;
  readonly error: string | null;
  /** Whether a scope AND a capability are chosen; nothing is read until both are. */
  readonly hasSelection: boolean;
}

function explanationText(explanation: ResolutionExplanation): string {
  switch (explanation.kind) {
    case "in-force":
      return "Set at the selected scope, and in force there.";
    case "default":
      return "No grant applies, so the capability default is in force.";
    case "inherited":
      return `Nothing is set here. Inherited from ${scopeLabel(explanation.from)}.`;
    case "clamped":
      return explanation.valueKind === "quota"
        ? `Clamped by ${scopeLabel(explanation.by)}, whose ceiling is lower than the value set here.`
        : `${scopeLabel(explanation.by)} takes precedence over the value set here.`;
    case "unavailable":
      return `The effective value could not be read: ${explanation.reason}. What is granted here is shown, but it is not necessarily what applies.`;
  }
}

function GrantedHalf({ view }: { readonly view: EffectiveVsGranted }): React.ReactElement {
  const { granted } = view;
  return (
    <div>
      <p className="text-xs uppercase text-muted-foreground">Granted at this scope</p>
      <p className="text-lg font-medium" data-testid="granted-value">
        {granted === null
          ? "Nothing set here"
          : formatEntitlementValue(granted.type, granted.value)}
      </p>
      {granted !== null && (
        <p className="text-xs text-muted-foreground" data-testid="granted-tier">
          {grantTierLabel(granted.tier)}
        </p>
      )}
    </div>
  );
}

function EffectiveHalf({ view }: { readonly view: EffectiveVsGranted }): React.ReactElement {
  const { effective } = view;
  const tagged = effective.resolved ? readTaggedValue(effective.value) : null;
  return (
    <div>
      <p className="text-xs uppercase text-muted-foreground">Effective</p>
      {tagged === null ? (
        <p className="text-lg font-medium text-muted-foreground" data-testid="effective-unresolved">
          Unavailable
        </p>
      ) : (
        <p className="text-lg font-medium" data-testid="effective-value">
          {formatEntitlementValue(tagged.kind, tagged.value)}
        </p>
      )}
      {/* Provenance belongs to a value that was read; without one it would describe nothing. */}
      {tagged !== null && effective.resolved && (
        <p className="text-xs text-muted-foreground" data-testid="effective-source">
          {precedenceTierLabel(effective.source.tier)} from {scopeLabel(effective.source.scope)}
        </p>
      )}
    </div>
  );
}

/**
 * Effective-vs-granted for the selected scope + capability (ENR-1130): what someone
 * set HERE next to what actually applies, and why they differ.
 *
 * The two halves come from two authorities and are never merged: `granted` is read
 * from the admin store with no resolution, `effective` from the same `evaluate()`
 * the enforcement path uses. The explanation is rendered from the server's
 * `inheritedFromElsewhere` rather than re-derived, so the console's wording cannot
 * drift from the decision it is describing.
 *
 * Presentational on purpose: the page owns the reading because the grant editor
 * needs the same `granted` value to seed its control from.
 */
export function ResolutionView({
  view,
  error,
  hasSelection,
}: ResolutionViewProps): React.ReactElement {
  if (!hasSelection) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="resolution-empty">
        Choose a scope and a capability to see what applies.
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid="resolution-error">
        {error}
      </p>
    );
  }
  if (view === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="resolution-loading">
        Reading the effective value
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="resolution-view">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <GrantedHalf view={view} />
        <EffectiveHalf view={view} />
      </div>
      <p className="text-sm text-muted-foreground" data-testid="resolution-explanation">
        {explanationText(explainResolution(view))}
      </p>
    </div>
  );
}
